import { useLayoutEffect, useRef, useState } from 'react'
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/20/solid'

import ExerciseCollection from 'components/ExerciseCollection/ExerciseCollection'

export default function ExerciseBlock({ 
    exerciseHashMap = {},
    programLevelHashMap = {},
    workoutPhaseTypeHashMap = {}, 
    exerciseBlock = null,
    enabledCanvasStyling = false,
    onHeightChange = () => {},
    showEquipmentFilteredExercises = false,
    highlightInjuryOverrides = false,
}) {
    const rootRef = useRef(null)
    const headerRef = useRef(null)
    const [expanded, setExpanded] = useState(false)

    useLayoutEffect(() => {
        const element = rootRef.current

        if (!element || !enabledCanvasStyling) return

        const reportHeight = () => {
            const header = headerRef.current
            const anchorOffset = header
                ? element.clientTop + header.offsetHeight / 2
                : element.offsetHeight / 2

            onHeightChange(exerciseBlock.exerciseBlockId, element.offsetHeight, anchorOffset)
        }

        reportHeight()

        const resizeObserver = new ResizeObserver(reportHeight)
        resizeObserver.observe(element)

        return () => resizeObserver.disconnect()
    }, [
        enabledCanvasStyling,
        exerciseBlock.exerciseBlockId,
        exerciseBlock?.exerciseCollections?.length,
        exerciseBlock?.description,
        expanded,
        onHeightChange,
    ])

    if (
        Object.keys(exerciseHashMap).length === 0
        || exerciseBlock === null
    ) {
        return null
    }

    const content = (
        <>
            <div
                ref={headerRef}
                className='p-4 flex items-center gap-x-4 cursor-pointer'
                onClick={() => setExpanded(!expanded)}
            >
                <p className='text-xs font-bold text-orange-400 flex-1'>
                    {workoutPhaseTypeHashMap[exerciseBlock.workoutPhaseTypeId]?.name || 'Unknown'}
                </p>
                
                <div>
                    <p className="text-left text-base font-medium text-gray-900">
                        {exerciseBlock.name}
                    </p>

                    {exerciseBlock?.description &&
                        <p className="text-right text-xs text-gray-500">
                            {exerciseBlock.description}
                        </p>
                    }
                </div>

                {expanded && <ChevronUpIcon className='w-6 h-6 text-gray-600' />}
                {!expanded && <ChevronDownIcon className='w-6 h-6 text-gray-600' />}
            </div>

            {expanded &&
                <div className='bg-gray-100 divide-y divide-gray-400'>
                    {exerciseBlock?.exerciseCollections?.length === 0 &&
                        <div className='p-2 text-center text-gray-500'>
                            No exercise collections
                        </div>
                    }
                    
                    {exerciseBlock?.exerciseCollections?.length > 0 && exerciseBlock?.exerciseCollections?.map((exerciseCollection, index) => (
                        <div 
                            key={index} 
                            className='w-full'
                        >
                            <ExerciseCollection
                                exerciseHashMap={exerciseHashMap}
                                programLevelHashMap={programLevelHashMap}
                                slotIndex={index} 
                                exerciseCollection={exerciseCollection}
                                showEquipmentFilteredExercises={showEquipmentFilteredExercises}
                                highlightInjuryOverrides={highlightInjuryOverrides}
                                showWorkoutSlots={enabledCanvasStyling}
                            />
                        </div>
                    ))}
                </div>
            }
        </>
    )

    if (enabledCanvasStyling) {
        return (
            <div
                ref={rootRef}
                className="overflow-visible rounded-2xl border border-gray-200 bg-white shadow-md"
                onPointerDown={(event) => event.stopPropagation()}
            >
                {content}
            </div>
        )
    }

    return (
        <div key={exerciseBlock.exerciseBlockId}>
            {content}
        </div>
    )
}